/**
 * 积分相关接口
 */
import request from '../xajax'
import type { ApiResponse, PageList } from '../interfaces'

export interface PointDaySign {
  id?: string
  aid?: string
  signDate?: string
  signPoints?: number
  continueDays?: number
  createTime?: string
}

export interface PointAccount {
  id?: string
  firstBuy?: boolean
  /** 可用积分 */
  usableValue?: number
  /** 永久积分 */
  userValue?: number
  /** 时效积分 */
  timedValue?: number
  timedJson?: string
  totalValue?: number
  usedValue?: number
  updateTime?: string
}

export interface PointPackage {
  id?: string
  name?: string
  oldPrice?: number
  nowPrice?: number
  points?: number
  bonus?: number
  hot?: boolean
}

export interface PointsRecord {
  id?: string
  aid?: string
  name?: string
  eventType?: string
  eventId?: string
  eventName?: string
  points?: number
  balance?: number
  thirdType?: string
  refType?: string
  refId?: string
  description?: string
  expireTime?: string
  sysCreateTime?: string
}

export interface PointsRecordQuery {
  eventType?: string
  aid?: string
  /** 1增 2减 */
  type?: number
  eventName?: string
  points?: number
  pageIndex?: number
  pageSize?: number
}

/** 支付客户端类型：桌面 / Web 统一用 web */
export type PayClientType = 'wxxcx' | 'wxgzh' | 'mobile' | 'web' | 'douyin'

export interface PointOrderPayRequest {
  packageId: string
  num?: number
  attach?: string
  virtual?: boolean
  clientType: PayClientType
}

/**
 * 下单结果：
 *  - web（桌面 / Web）：微信 Native 支付 → codeUrl，形如 weixin://wxpay/bizpayurl?pr=xxx，需要前端渲染成二维码
 *  - mobile：h5Url
 *  - wxgzh：JSAPI 参数（appId / timeStamp / package / paySign）
 *  - 小程序虚拟支付：offerId 等（桌面 / Web 不支持，前端会直接拒绝）
 */
export interface PointOrderPayResult {
  codeUrl?: string
  h5Url?: string
  offerId?: string
  orderId?: string
  orderNo?: string
  [key: string]: any
}

export const createWxPay = (data: PointOrderPayRequest): Promise<ApiResponse<PointOrderPayResult>> =>
  request.post('/client/point/order/wxpay/create', data)

/**
 * 检查订单支付状态，使用下单返回的 orderId 查询。
 * code === 0 且 data === true 表示已支付成功。
 * @param param orderId：下单接口（createWxPay / createPointOrder）返回的订单 id
 */
export const checkPointOrder = ({ orderId }: { orderId: string }): Promise<ApiResponse<boolean>> =>
  request.post('/client/point/order/wxpay/check', { orderId })
/**
 * 桌面 / Web 购买积分下单
 *
 * 固定 clientType=web + virtual=false：走微信 Native 支付生成付款二维码，
 * 绝不走微信小程序虚拟支付（虚拟支付只有小程序端可用）。
 */
export const createPointOrder = (packageId: string, num = 1): Promise<ApiResponse<PointOrderPayResult>> =>
  createWxPay({ packageId, num, clientType: 'web', virtual: false })

export const doSign = (): Promise<ApiResponse<number>> => request.get('/client/point/day/dosign')
export const getWeekSign = (): Promise<ApiResponse<PointDaySign[]>> => request.get('/client/point/day/week')
export const getMonthSign = (): Promise<ApiResponse<PointDaySign[]>> => request.get('/client/point/day/month')
export const getUserPoints = (): Promise<ApiResponse<PointAccount>> => request.get('/client/point/user-points')

export const apiPointClient = {
  getPackages: (): Promise<ApiResponse<PointPackage[]>> => request.get('/client/point/packages'),
  getRecordList: (data: PointsRecordQuery): Promise<ApiResponse<PageList<PointsRecord>>> =>
    request.post('/client/point/record/list', data),
  removeRecordBatch: (ids: string[]): Promise<ApiResponse<boolean>> =>
    request.post('/client/point/record/remove-batch', ids)
}

export const apiPoints = {
  getRecords: (data: PointsRecordQuery = {}) =>
    apiPointClient.getRecordList({ pageIndex: 1, pageSize: 100, ...data }),
  getPackages: () => apiPointClient.getPackages()
  // 购买走 createWxPay（/client/point/order/wxpay/create），没有独立的「购买」接口
}
